/**
 * ============================================================================
 * RequalificationsTable — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: rows for past and pending requalifications. Includes trigger
 *          source chip (CALENDAR / CHANGE_CONTROL / DEVIATION / LOCATION_CHANGE)
 *          and the completion state. Click → drill into the linked
 *          qualification document where one exists.
 *
 * A11Y: chips carry both a coloured background and a textual label.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EquipmentDetail.module.css';
import type {
  RequalificationRow,
  RequalificationState,
  RequalificationTrigger,
} from './useEquipmentDetailQuery';

const STATE_CLASS: Record<RequalificationState, string> = {
  PENDING: styles.lifeEarly,
  IN_PROGRESS: styles.lifeRestricted,
  COMPLETED: styles.lifeQualified,
};

const TRIGGER_CLASS: Record<RequalificationTrigger, string> = {
  CALENDAR: styles.gamp1,
  CHANGE_CONTROL: styles.gamp3,
  DEVIATION: styles.critMajor,
  LOCATION_CHANGE: styles.gamp4,
};

export interface RequalificationsTableProps {
  rows: RequalificationRow[];
}

export function RequalificationsTable({ rows }: RequalificationsTableProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('equipment.detail.requalifications.empty', {
          defaultValue: 'No requalifications scheduled or completed yet.',
        })}
      </p>
    );
  }

  return (
    <table
      className={styles.dataTable}
      aria-label={t('equipment.detail.requalifications.tableAria', {
        defaultValue: 'Requalifications',
      })}
    >
      <caption className={styles.srOnly}>
        {t('equipment.detail.requalifications.tableCaption', {
          defaultValue:
            'Past and pending requalifications with trigger source, scheduled date, completion state, and qualification document id.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.scheduled', {
              defaultValue: 'Scheduled',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.trigger', {
              defaultValue: 'Trigger',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.state', {
              defaultValue: 'State',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.started', {
              defaultValue: 'Started',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.completed', {
              defaultValue: 'Completed',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.lead', {
              defaultValue: 'Validation lead',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.requalifications.columns.document', {
              defaultValue: 'Document',
            })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.requalification_id}>
            <td>
              <time dateTime={r.scheduled_for_utc}>
                {dateFmt.format(new Date(r.scheduled_for_utc))}
              </time>
            </td>
            <td>
              <span
                className={`${styles.chip} ${TRIGGER_CLASS[r.trigger]}`}
                aria-label={t('equipment.detail.requalifications.triggerAria', {
                  defaultValue: 'Trigger: {{trigger}}',
                  trigger: t(
                    `equipment.detail.requalifications.trigger.${r.trigger}`,
                    { defaultValue: r.trigger },
                  ),
                })}
              >
                {t(`equipment.detail.requalifications.trigger.${r.trigger}`, {
                  defaultValue: r.trigger,
                })}
              </span>
            </td>
            <td>
              <span className={`${styles.chip} ${STATE_CLASS[r.state]}`}>
                {t(`equipment.detail.requalifications.state.${r.state}`, {
                  defaultValue: r.state,
                })}
              </span>
            </td>
            <td>
              {r.started_at_utc ? (
                <time dateTime={r.started_at_utc}>
                  {dateFmt.format(new Date(r.started_at_utc))}
                </time>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
            <td>
              {r.completed_at_utc ? (
                <time dateTime={r.completed_at_utc}>
                  {dateFmt.format(new Date(r.completed_at_utc))}
                </time>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
            <td>
              {r.validation_lead ?? <span className={styles.subdued}>—</span>}
            </td>
            <td>
              {r.qualification_document_id ? (
                <code className={styles.codeChip}>
                  {r.qualification_document_id}
                </code>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
